import React, { FunctionComponent } from 'react'
import { add, vn } from 'vend-number'
import { T } from '@transifex/react'
import { formatCurrencyValue } from '@vend/utilities'
import classNames from 'classnames'
import {
  useRegisterManager,
  useRetailerSettingsManager,
} from '../../../ng-compat/hooks'
import { useAppSelector } from '../../../state/hooks'
import { SaleDiscount } from '../../../models/sale-discount'
import { selectSaleSummaryFields } from '../../../state/slices/currentSale/currentSaleSlice'

/**
 * Calculate the discount value based on tax inclusivity
 * @returns The discount value to display (negated)
 */
const getDiscount = (
  discount: SaleDiscount | null | undefined,
  isTaxInclusive: boolean
): number => {
  let discountValue = 0

  if (discount && discount.price) {
    const { price, tax } = discount

    if (isTaxInclusive) {
      discountValue = add(price, tax)
    } else {
      discountValue = vn(price).toNumber()
    }
  }

  if (vn(discountValue).eq(0)) {
    return 0 // No need to negate if discount is $0
  }

  return -discountValue // Negate for display purposes
}

export const SaleDiscountDisplay: FunctionComponent = () => {
  const { discount } = useAppSelector(selectSaleSummaryFields)
  const retailer = useRetailerSettingsManager().get()
  const registerManager = useRegisterManager()
  const currentOutlet = registerManager.getActiveOutlet()

  const isTaxInclusive = registerManager.isTaxInclusive()

  return (
    <li
      className={classNames('sale-summary-line', {
        'vd-pt2': !isTaxInclusive, // only apply padding if tax excl because it won't be the first item
      })}
    >
      <div data-cy="discount-label" className="sale-summary-line-item--label">
        <T _str="Additional Discount" _context="Additional discount for sale" />
      </div>
      <b className="sale-summary-line-item--value">
        {formatCurrencyValue(
          getDiscount(discount, isTaxInclusive),
          retailer.culture,
          currentOutlet.currency,
          currentOutlet.currency_symbol
        )}
      </b>
    </li>
  )
}
